// 3rd party libs
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store/store';
import {
  isCloudFrontCookiesEnabledForCurrentHost,
  issueCloudFrontSignedCookies,
} from './api/videofeeds';
import {
  ACCESS_TOKEN_UPDATED_EVENT,
  getTokenFromLocalStorage,
} from './helpers/localStorage';
import { PATHS } from './enums/PATHS';

// local imports
import App from './App';
import './assets/scss/CustomScrollbar.scss';
import './index.css';

const container = document.getElementById('root')!;
const root = createRoot(container);
const CLOUDFRONT_REFRESH_BUFFER_SECONDS = 300;
let cloudFrontRefreshTimeout: number | null = null;

const shouldRefreshCloudFrontCookies = () =>
  isCloudFrontCookiesEnabledForCurrentHost() &&
  window.location.pathname !== PATHS.ADMIN_PAGE.path &&
  !window.location.pathname.startsWith(`${PATHS.ADMIN_PAGE.path}/`);

const renderApp = () => {
  root.render(
    <Provider store={store}>
      <App />
    </Provider>,
  );
};

const scheduleCloudFrontCookieRefresh = (expiresAt: number) => {
  if (typeof window === 'undefined') return;

  if (cloudFrontRefreshTimeout !== null) {
    window.clearTimeout(cloudFrontRefreshTimeout);
  }

  if (!expiresAt) return;

  const delayMs = Math.max(
    0,
    (expiresAt - Math.floor(Date.now() / 1000) - CLOUDFRONT_REFRESH_BUFFER_SECONDS) *
      1000,
  );

  cloudFrontRefreshTimeout = window.setTimeout(() => {
    void refreshCloudFrontCookies(true);
  }, delayMs);
};

const refreshCloudFrontCookies = async (forceRefresh = false) => {
  if (!shouldRefreshCloudFrontCookies()) {
    return;
  }

  try {
    const response = await issueCloudFrontSignedCookies('', { forceRefresh });
    scheduleCloudFrontCookieRefresh(response?.expiresAt || 0);
  } catch {}
};

const onAccessTokenUpdated = () => {
  if (!shouldRefreshCloudFrontCookies()) return;
  if (!getTokenFromLocalStorage()) return;

  void refreshCloudFrontCookies(true);
};

const bootstrap = async () => {
  if (shouldRefreshCloudFrontCookies()) {
    await refreshCloudFrontCookies();

    window.addEventListener('focus', () => {
      void refreshCloudFrontCookies();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void refreshCloudFrontCookies();
      }
    });
    window.addEventListener(
      ACCESS_TOKEN_UPDATED_EVENT,
      onAccessTokenUpdated as EventListener,
    );
  }

  renderApp();
};

void bootstrap();
